/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Rocket, Send, StopCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useCloseSurveyCampaign,
  useLaunchCampaign,
  useSendCampaignEmails,
  useSurveyCampaign,
  useSurveyCampaignResponses,
} from '@/features/surveys/api/use-survey-campaigns';
import { useSurveyTemplate } from '@/features/surveys/api/use-survey-templates';
import { AddRecipientsDialog } from '@/features/surveys/components/AddRecipientsDialog';
import { CampaignResponsesPanel } from '@/features/surveys/components/CampaignResponsesPanel';
import { DataMappingEditor } from '@/features/surveys/components/DataMappingEditor';
import { LandingStatus } from '@/features/surveys/components/LandingStatus';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/surveys/campaigns/$campaignId')({
  component: CampaignDetailPage,
});

function CampaignDetailPage() {
  const { t } = useTranslation('surveys');
  // TanStack router params are typed correctly at runtime here, but this route file
  // currently resolves to an error-typed helper in ESLint's type analysis.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const params = Route.useParams();
  const campaignId = typeof params.campaignId === 'string' ? params.campaignId : '';
  const { data: campaign, isLoading } = useSurveyCampaign(campaignId);
  const { data: responsesData, isLoading: responsesLoading } = useSurveyCampaignResponses(campaignId);
  const { data: template } = useSurveyTemplate(campaign?.templateId);
  const closeMutation = useCloseSurveyCampaign();
  const launchMutation = useLaunchCampaign();
  const sendMutation = useSendCampaignEmails();
  const [activeTab, setActiveTab] = useState('overview');
  const [addRecipientsOpen, setAddRecipientsOpen] = useState(false);

  const handleClose = async () => {
    if (!campaignId) return;
    try {
      await closeMutation.mutateAsync(campaignId);
      toast.success(t('toast.campaignClosedSuccessfully'));
    } catch {
      toast.error(t('toast.failedToCloseCampaign'));
    }
  };

  const handleLaunch = async () => {
    if (!campaignId) return;
    try {
      await launchMutation.mutateAsync({
        campaignId,
        data: { sendEmails: true },
      });
      toast.success(t('toast.campaignLaunchedSuccessfully'));
    } catch {
      toast.error(t('toast.failedToLaunchCampaign'));
    }
  };

  const handleSendEmails = async () => {
    if (!campaignId) return;
    try {
      const result = await sendMutation.mutateAsync({
        campaignId,
        data: { filter: 'non_submitted' },
      });
      toast.success(t('toast.sentEmails', { result: String(result.emailsSent) }));
    } catch {
      toast.error(t('toast.failedToSendEmails'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!campaign) {
    return <div className="p-6 text-muted-foreground">{t('campaignDetail.notFound')}</div>;
  }

  const progressPct =
    campaign.progress.total > 0
      ? Math.round((campaign.progress.submitted / campaign.progress.total) * 100)
      : 0;
  const landingMode =
    typeof campaign.dataTargetConfig?.landing_mode === 'string'
      ? campaign.dataTargetConfig.landing_mode
      : 'manual';

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/surveys/campaigns" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold">{campaign.name}</h1>
          <Badge
            variant={
              campaign.status === 'active'
                ? 'default'
                : campaign.status === 'draft'
                  ? 'outline'
                  : 'secondary'
            }
          >
            {campaign.status}
          </Badge>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'draft' && (
            <Button
              onClick={() => void handleLaunch()}
              disabled={launchMutation.isPending}
              data-testid="launch-campaign-button"
            >
              <Rocket className="mr-2 h-4 w-4" />
              {launchMutation.isPending ? t('campaignDetail.launching') : t('campaignDetail.launchCampaign')}
            </Button>
          )}
          {campaign.status === 'active' && (
            <>
              <Button
                variant="outline"
                onClick={() => void handleSendEmails()}
                disabled={sendMutation.isPending}
                data-testid="send-emails-button"
              >
                <Send className="mr-2 h-4 w-4" />
                {sendMutation.isPending ? t('campaignDetail.sending') : t('campaignDetail.sendInvitations')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleClose()}
                disabled={closeMutation.isPending}
                data-testid="close-campaign-button"
              >
                <StopCircle className="mr-2 h-4 w-4" />
                {closeMutation.isPending ? t('campaignDetail.closing') : t('campaignDetail.closeCampaign')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Campaign Info */}
      <div className="grid grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">{t('campaignDetail.template')}:</span>{' '}
          <span className="font-medium">{campaign.templateName}</span>
        </div>
        <div>
          <span className="text-muted-foreground">{t('campaignDetail.domain')}:</span>{' '}
          <span className="font-medium">{campaign.domainCode}</span>
        </div>
        <div>
          <span className="text-muted-foreground">{t('campaignDetail.recipientType')}:</span>{' '}
          <span className="font-medium">{campaign.recipientType}</span>
        </div>
        <div>
          <span className="text-muted-foreground">{t('campaignDetail.deadline')}:</span>{' '}
          <span className="font-medium">
            {new Date(campaign.deadline).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            {t('campaignDetail.overview')}
          </TabsTrigger>
          <TabsTrigger value="recipients" data-testid="tab-recipients">
            {t('campaignDetail.recipients')}
          </TabsTrigger>
          <TabsTrigger value="responses" data-testid="tab-responses">
            {t('campaignDetail.responses')}
          </TabsTrigger>
          <TabsTrigger value="mapping" data-testid="tab-mapping">
            {t('campaignDetail.dataMapping')}
          </TabsTrigger>
          <TabsTrigger value="landing" data-testid="tab-landing">
            {t('campaignDetail.landing')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('campaignDetail.totalRecipients')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold" data-testid="progress-total">
                  {campaign.progress.total}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('campaignDetail.submitted')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600" data-testid="progress-submitted">
                  {campaign.progress.submitted}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('campaignDetail.inProgress')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-blue-600">
                  {campaign.progress.inProgress}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('campaignDetail.overdue')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-red-600">{campaign.progress.overdue}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('campaignDetail.responseProgress')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Progress value={progressPct} className="h-3" />
              <p className="mt-2 text-sm text-muted-foreground">{t('campaignDetail.percentComplete', { pct: progressPct })}</p>
            </CardContent>
          </Card>

          {campaign.sentAt && (
            <p className="text-sm text-muted-foreground">
              {t('campaignDetail.launched')}: {new Date(campaign.sentAt).toLocaleString()}
            </p>
          )}
        </TabsContent>

        <TabsContent value="recipients">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('campaignDetail.recipients')}</CardTitle>
                {(campaign.status === 'draft' || campaign.status === 'active') && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="add-recipients-button"
                    onClick={() => { setAddRecipientsOpen(true); }}
                  >
                    {t('campaignDetail.addRecipients')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('campaignDetail.name')}</TableHead>
                    <TableHead>{t('campaignDetail.email')}</TableHead>
                    <TableHead>{t('campaignDetail.status')}</TableHead>
                    <TableHead>{t('campaignDetail.surveyLink')}</TableHead>
                    <TableHead>{t('campaignDetail.sentAt')}</TableHead>
                    <TableHead>{t('campaignDetail.submittedAt')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaign.recipients.map((r) => (
                    <TableRow key={r.id} data-testid={`recipient-row-${r.id}`}>
                      <TableCell>{r.name ?? '-'}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === 'submitted' ? 'default' : 'outline'}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.token ? (
                          <a
                            href={`/survey-respond/${r.token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline"
                          >
                            {t('campaignDetail.openSurvey')}
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {r.sentAt ? new Date(r.sentAt).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell>
                        {r.submittedAt ? new Date(r.submittedAt).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="responses">
          {responsesLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <CampaignResponsesPanel
              responses={responsesData?.items ?? []}
              templateSections={template?.sections ?? []}
            />
          )}
        </TabsContent>

        <TabsContent value="mapping">
          <Card>
            <CardHeader>
              <CardTitle>{t('campaignDetail.dataMapping')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">
                {t('campaignDetail.dataMappingDesc')}
              </p>
              {campaign.dataTargetConfig && (
                <div className="mb-4 rounded-md bg-muted/50 p-3 text-sm">
                  <p>
                    {t('campaignDetail.landingMode')}:{' '}
                    <strong>
                      {landingMode}
                    </strong>
                  </p>
                </div>
              )}
              {template?.sections && (
                <DataMappingEditor
                  campaignId={campaignId}
                  templateSections={template.sections}
                  readOnly={campaign.status === 'closed' || campaign.status === 'completed'}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="landing">
          <LandingStatus campaignId={campaignId} campaignStatus={campaign.status} />
        </TabsContent>
      </Tabs>

      <AddRecipientsDialog
        campaignId={campaignId}
        recipientType={campaign.recipientType}
        existingRecipients={campaign.recipients}
        open={addRecipientsOpen}
        onOpenChange={setAddRecipientsOpen}
      />
    </div>
  );
}
